/* Workspace — Admin Tools (Token usage)
 *
 *  Endpoints:
 *    GET /api/admin/token-usage   (legacy role==admin)
 *      → { total_tokens, total_tasks, today_tokens, today_tasks,
 *           per_user: [{ username, tokens, tasks }] }
 */

/* Expressive 頁面專屬 CSS：KPI 列（GX 才掛的 .gx-p-adm-kpis）≤767 時 index.html 把 rwd-grid-4 的 gap 歸 0 */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-admin-tools', String.raw`
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-page .gx-p-adm-kpis{gap:8px!important;grid-template-columns:repeat(2,minmax(0,1fr))!important}
}
`);

function AdminToolsPage() {
  const toast = useToast();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api('GET', '/admin/token-usage')
      .then(d => setData(d))
      .catch(e => toast(e.detail || 'Token 統計載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Sort users by token usage desc
  const ranked = (data?.per_user || [])
    .slice()
    .sort((a, b) => (b.tokens || 0) - (a.tokens || 0));
  const maxTokens = Math.max(1, ...ranked.map(u => u.tokens || 0));

  return (
    <>
      <PageHeader
        eyebrow="管理員工具 · Admin"
        title="Token 使用統計"
        italic="AI Usage"
        action={
          <Button
            variant="secondary"
            icon="refresh"
            onClick={() => { setLoading(true); api('GET', '/admin/token-usage').then(setData).finally(() => setLoading(false)); }}
            disabled={loading}
          >重新整理</Button>
        }
      />

      {/* KPI strip */}
      <div className={GxCore.cls('rwd-grid-4', 'gx-p-adm-kpis')} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { eyebrow: '今日 Token', value: data?.today_tokens, italic: '本日累計', highlight: HR.accent },
          { eyebrow: '今日任務數', value: data?.today_tasks, italic: '次數' },
          { eyebrow: '總 Token',   value: data?.total_tokens, italic: '歷史累計' },
          { eyebrow: '總任務數',   value: data?.total_tasks, italic: '次數' },
        ].map((s, i) => (
          <div key={i} style={uiStyle({
            padding: '18px 22px', background: HR.surface,
            border: `1px solid ${HR.rule}`, borderRadius: 6,
          }, { padding: '18px 20px', border: 'none', borderRadius: GxT.radius.card })}>
            <div style={uiStyle({
              fontSize: FontSizes.trading_md, color: HR.inkFaint,
              letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8,
            }, {
              fontSize: GxT.size.label, color: GxT.color.onV, fontWeight: GxT.weight.label,
              letterSpacing: 0, textTransform: 'none',
            })}>{s.eyebrow}</div>
            <div style={uiStyle({
              fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, lineHeight: 1,
              color: s.highlight || HR.ink, fontVariantNumeric: 'tabular-nums',
            }, { fontSize: 'clamp(28px, 3.2vw, 44px)', fontWeight: GxT.weight.title, letterSpacing: '-0.5px' })}>{loading ? '—' : (s.value ?? '—').toLocaleString()}</div>
            <div style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 6 }, { marginTop: 8 })}>{s.italic}</div>
          </div>
        ))}
      </div>

      <Card
        padding="0"
        eyebrow="Leaderboard"
        title="使用者 Token 排行"
        italic={`${ranked.length} 位使用者`}
      >
        {loading ? (
          <SkeletonTable rows={8} cols={[40, '1.5fr', '1fr', '1fr', '2fr']} />
        ) : ranked.length === 0 ? (
          <EmptyState icon="search" title="尚無 token 使用紀錄" hint="使用者透過 /api/research 或 /api/ai/chat 後會出現在這裡。" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500, width: 50 }}>#</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>使用者</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500, width: 130 }}>Token</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500, width: 100 }}>任務數</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left', fontWeight: 500 }}>分佈</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((u, i) => {
                const pct = ((u.tokens || 0) / maxTokens) * 100;
                return (
                  <tr key={u.username} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{
                      padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs,
                      color: i < 3 ? HR.accent : HR.inkFaint, fontWeight: i < 3 ? 600 : 400,
                    }}>{i + 1}</td>
                    <td style={{ padding: '13px 16px', fontWeight: 500 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 24, height: 24, borderRadius: '50%',
                          background: uiPick('#D6CEB9', GxT.color.tc), color: uiPick(HR.ink, GxT.color.onTc),
                          display: 'grid', placeItems: 'center',
                          fontSize: FontSizes.trading_md, fontWeight: uiPick(600, GxT.weight.title), fontFamily: HR.serif,
                        }}>{(u.username || '?').slice(0, 1).toUpperCase()}</span>
                        @{u.username}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 500 }}>
                      {(u.tokens || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>
                      {(u.tasks || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px' }}>
                      <div style={{
                        height: 6, background: HR.ruleSoft, borderRadius: 3, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          background: HR.accent, transition: 'width 200ms',
                        }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

window.AdminToolsPage = AdminToolsPage;
